import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getQuizCards, Card } from '../api';

type Mode = 'select' | 'practice' | 'test';

export default function TypingPage() {
  const { id } = useParams<{ id: string }>();
  const deckId = Number(id);
  const [mode, setMode] = useState<Mode>('select');

  const [cards, setCards] = useState<Card[]>([]);
  const [qIndex, setQIndex] = useState(0);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wrongList, setWrongList] = useState<Card[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [practiceCorrectCount, setPracticeCorrectCount] = useState(0);
  const [practiceWrongCount, setPracticeWrongCount] = useState(0);

  const loadCards = useCallback(async () => {
    setLoading(true);
    const cards = await getQuizCards(deckId, 10);
    setCards(cards.length >= 1 ? cards : []);
    resetState();
    setLoading(false);
  }, [deckId]);

  const resetState = () => {
    setQIndex(0);
    setInput('');
    setScore(0);
    setDone(false);
    setWrongList([]);
    setFeedback(null);
    setPracticeCorrectCount(0);
    setPracticeWrongCount(0);
  };

  useEffect(() => { loadCards(); }, [loadCards]);

  const current = cards[qIndex];
  const isJa = current?.front?.match(/[\u3040-\u30ff\u4e00-\u9faf]/);
  const normalizeInput = (text: string) => text.trim().toLowerCase();
  const isCorrect = normalizeInput(input) === normalizeInput(current?.front || '');

  // ─── PRACTICE MODE ─────────────────────────────────────────────────────────
  const handlePracticeInput = (value: string) => {
    setInput(value);
    if (value.trim()) {
      const correct = normalizeInput(value) === normalizeInput(current?.front || '');
      if (correct && !feedback) {
        setPracticeCorrectCount(c => c + 1);
        setFeedback('correct');
      } else if (!correct && feedback === 'correct') {
        setPracticeWrongCount(w => w + 1);
        setFeedback('wrong');
      }
    } else {
      setFeedback(null);
    }
  };

  const handlePracticeNext = () => {
    if (qIndex + 1 >= cards.length) {
      setMode('select');
      resetState();
    } else {
      setQIndex(i => i + 1);
      setInput('');
      setFeedback(null);
    }
  };

  // ─── TEST MODE ─────────────────────────────────────────────────────────────
  const handleTestSubmit = () => {
    if (!input.trim()) return;

    if (isCorrect) {
      setScore(s => s + 1);
      setFeedback('correct');
    } else {
      setFeedback('wrong');
      setWrongList(w => [...w, current]);
    }

    setTimeout(() => {
      if (qIndex + 1 >= cards.length) {
        setDone(true);
      } else {
        setQIndex(i => i + 1);
        setInput('');
        setFeedback(null);
      }
    }, 1200);
  };

  const handleTestRetryAll = async () => {
    await loadCards();
    setMode('test');
  };

  const handleTestRetryWrong = () => {
    if (wrongList.length === 0) {
      setMode('select');
      resetState();
      return;
    }
    setCards(wrongList);
    setQIndex(0);
    setInput('');
    setScore(0);
    setDone(false);
    setWrongList([]);
    setFeedback(null);
  };

  const handleTestKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && feedback === null) {
      handleTestSubmit();
    }
  };

  const getEmoji = () => {
    const pct = score / cards.length;
    if (pct >= 0.9) return '🏆';
    if (pct >= 0.7) return '🎉';
    if (pct >= 0.5) return '💪';
    return '📚';
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────

  if (loading) return <div className="loading"><div className="spinner" />Đang chuẩn bị...</div>;

  if (cards.length === 0) return (
    <div className="quiz-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
      <div className="empty-state">
        <div className="empty-icon">⌨️</div>
        <p>Cần ít nhất 1 thẻ để làm bài tập typing.</p>
        <Link to={`/deck/${deckId}`} className="btn btn-primary">+ Thêm thẻ</Link>
      </div>
    </div>
  );

  // ─── MODE SELECT ───────────────────────────────────────────────────────────
  if (mode === 'select') {
    return (
      <div className="quiz-page">
        <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
        <div className="mode-select">
          <h2>Chọn chế độ luyện tập</h2>
          <div className="mode-cards">
            <button className="mode-card" onClick={() => { setMode('practice'); resetState(); }}>
              <div className="mode-icon">🎯</div>
              <div className="mode-title">Luyện tập</div>
              <div className="mode-desc">Xem đầu đủ từ gốc, gõ và kiểm tra real-time. Phù hợp để ghi nhớ.</div>
            </button>
            <button className="mode-card" onClick={() => { setMode('test'); resetState(); }}>
              <div className="mode-icon">✅</div>
              <div className="mode-title">Kiểm tra</div>
              <div className="mode-desc">Chỉ xem nghĩa, gõ từ, kiểm tra kết quả. Phù hợp để đánh giá.</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── PRACTICE MODE ────────────────────────────────────────────────────────
  if (mode === 'practice') {
    return (
      <div className="quiz-page">
        <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>

        <div className="flashcard-progress">
          <div className="progress-bar"><div className="progress-fill" style={{ width: `${(qIndex / cards.length) * 100}%`, background: 'var(--accent)' }} /></div>
          <div className="progress-text">Câu {qIndex + 1}/{cards.length} · Đúng: {practiceCorrectCount} | Sai: {practiceWrongCount}</div>
        </div>

        <div className="quiz-question">
          <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
            Luyện tập: Xem từ gốc, gõ từ gốc
          </div>
          <div className="quiz-word">{current.back}</div>
          {isJa && current.reading && (
            <div className="quiz-reading">{current.reading}</div>
          )}
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--paper-warm)', borderRadius: '10px', fontSize: '1.2rem', fontWeight: '600' }}>
            {current.front}
          </div>
        </div>

        <div className="typing-input-wrap">
          <input
            type="text"
            className={`typing-input ${feedback === 'correct' ? 'correct' : feedback === 'wrong' ? 'wrong' : ''}`}
            value={input}
            onChange={e => handlePracticeInput(e.target.value)}
            placeholder="Gõ từ gốc..."
            autoFocus
            lang={isJa ? 'ja' : 'en'}
            inputMode="text"
            spellCheck={false}
            autoCorrect="off"
            style={{
              borderColor: feedback === 'correct' ? '#10b981' : feedback === 'wrong' ? '#ef4444' : 'var(--border)',
              backgroundColor: feedback === 'correct' ? '#ecfdf5' : feedback === 'wrong' ? '#fef2f2' : 'white',
            }}
          />
          {feedback === 'correct' && <div style={{ color: '#10b981', fontSize: '0.875rem', marginTop: '0.5rem', fontWeight: '600' }}>✓ Chính xác!</div>}
          {feedback === 'wrong' && <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem', fontWeight: '600' }}>✗ Sai rồi</div>}
        </div>

        <button
          className="btn btn-primary btn-lg"
          onClick={handlePracticeNext}
          style={{ marginTop: '1.5rem', width: '100%' }}
        >
          {qIndex + 1 >= cards.length ? 'Hoàn thành' : 'Tiếp theo'}
        </button>
      </div>
    );
  }

  // ─── TEST MODE - DONE ──────────────────────────────────────────────────────
  if (mode === 'test' && done) {
    return (
      <div className="quiz-page">
        <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
        <div className="quiz-result">
          <div className="result-emoji">{getEmoji()}</div>
          <div className="result-score">{score}/{cards.length}</div>
          <div className="result-label">câu đúng · {Math.round((score / cards.length) * 100)}%</div>

          {wrongList.length > 0 && (
            <div style={{ textAlign: 'left', background: '#fee2e2', borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Cần ôn lại ({wrongList.length} từ):
              </div>
              {wrongList.map(c => (
                <div key={c.id} style={{ fontSize: '0.875rem', padding: '3px 0', color: '#7f1d1d' }}>
                  <strong>{c.back}</strong> → {c.front}
                </div>
              ))}
            </div>
          )}

          <div className="result-actions">
            <button className="btn btn-accent btn-lg" onClick={handleTestRetryAll}>🔄 Test lại tất cả</button>
            {wrongList.length > 0 && (
              <button className="btn btn-gold btn-lg" onClick={handleTestRetryWrong}>🎯 Test lại từ sai</button>
            )}
            <Link to={`/deck/${deckId}`} className="btn btn-ghost btn-lg">← Về bộ từ</Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── TEST MODE - QUESTIONS ───────────────────────────────────────────────
  const progress = ((qIndex) / cards.length) * 100;
  return (
    <div className="quiz-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>

      <div className="flashcard-progress">
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--accent)' }} /></div>
        <div className="progress-text">Câu {qIndex + 1}/{cards.length} · Điểm: {score}</div>
      </div>

      <div className="quiz-question">
        <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
          Gõ từ gốc dựa trên nghĩa này
        </div>
        <div className="quiz-word">{current.back}</div>
        {isJa && current.reading && (
          <div className="quiz-reading">{current.reading}</div>
        )}
      </div>

      <div className="typing-input-wrap">
        <input
          type="text"
          className={`typing-input ${feedback === 'correct' ? 'correct' : feedback === 'wrong' ? 'wrong' : ''}`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleTestKeyDown}
          placeholder="Nhập từ cần tìm..."
          autoFocus
          disabled={feedback !== null}
          lang={isJa ? 'ja' : 'en'}
          inputMode="text"
          spellCheck={false}
          autoCorrect="off"
          style={{
            borderColor: feedback === 'correct' ? '#10b981' : feedback === 'wrong' ? '#ef4444' : 'var(--border)',
            backgroundColor: feedback === 'correct' ? '#ecfdf5' : feedback === 'wrong' ? '#fef2f2' : 'white',
          }}
        />
        {feedback === 'correct' && <div style={{ color: '#10b981', fontSize: '0.875rem', marginTop: '0.5rem' }}>✓ Đúng rồi!</div>}
        {feedback === 'wrong' && (
          <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            ✗ Sai! Đáp án: <strong>{current.front}</strong>
          </div>
        )}
      </div>

      <button
        className="btn btn-primary btn-lg"
        onClick={handleTestSubmit}
        disabled={!input.trim() || feedback !== null}
        style={{ marginTop: '1.5rem', width: '100%' }}
      >
        {feedback ? 'Đợi...' : 'Gửi (Enter)'}
      </button>
    </div>
  );
}
